/**
 * ============================================================================
 * [P8-01] ReferenceSelectionPanel - 참고 레퍼런스 선택 패널
 *
 * 기능:
 * - 접을 수 있는(collapsible) 레퍼런스 선택 영역
 * - ReferenceLoader 모달을 통한 레퍼런스 선택
 * - 선택된 레퍼런스를 태그로 표시 (최대 3개)
 * - 태그 × 버튼으로 제거
 *
 * [2026-03-02] 신규 생성
 * ============================================================================
 */

// ============================================================================
// 1. Imports
// ============================================================================
import { useState, useCallback } from 'react'
import { useAppStore, useSelectedReferences } from '@/stores'
import { ReferenceLoader } from '@/components/management/ReferenceLoader'
import { ReferenceAnalysisModal } from '@/components/reference-analysis'
import type { ReferenceItem, SavedText } from '@/types'
import styles from './ReferenceSelectionPanel.module.css'

// ============================================================================
// 2. Helper: ReferenceItem → SavedText 변환
// ============================================================================
function referenceItemToSavedText(item: ReferenceItem): SavedText {
  return {
    id: item.id,
    content: item.content,
    title: item.title,
    type: item.sourceType === 'tracking' ? 'edit' : 'reference',
    topic: item.category ?? '',
    charCount: item.content.length,
    createdAt: item.createdAt,
    updatedAt: item.createdAt,
    userId: '',
  }
}

function savedTextToReferenceItem(text: SavedText): ReferenceItem {
  return {
    id: text.id,
    title: text.title,
    content: text.content,
    sourceType: 'saved',
    category: text.topic,
    createdAt: text.createdAt,
  }
}

// ============================================================================
// 3. ReferenceSelectionPanel Component
// ============================================================================
export function ReferenceSelectionPanel() {
  // ========================================================================
  // 3-1. 상태
  // ========================================================================
  const [isCollapsed, setIsCollapsed] = useState(true)
  const [isLoaderOpen, setIsLoaderOpen] = useState(false)
  const [analysisSource, setAnalysisSource] = useState<ReferenceItem | null>(null)
  const [isAnalysisOpen, setIsAnalysisOpen] = useState(false)

  // ========================================================================
  // 3-2. Store 연동
  // ========================================================================
  const selectedReferences = useSelectedReferences()
  const addSelectedReference = useAppStore((s) => s.addSelectedReference)
  const removeSelectedReference = useAppStore((s) => s.removeSelectedReference)

  // ========================================================================
  // 3-3. 핸들러
  // ========================================================================
  const handleToggle = useCallback(() => {
    setIsCollapsed((prev) => !prev)
  }, [])

  const handleOpenLoader = useCallback(() => {
    setIsLoaderOpen(true)
  }, [])

  const handleCloseLoader = useCallback(() => {
    setIsLoaderOpen(false)
  }, [])

  const handleSelectReference = useCallback(
    (item: ReferenceItem) => {
      const converted = referenceItemToSavedText(item)
      addSelectedReference(converted)
    },
    [addSelectedReference]
  )

  const handleAnalyzeReference = useCallback((item: ReferenceItem) => {
    setAnalysisSource(item)
    setIsAnalysisOpen(true)
  }, [])

  const handleAnalyzeSelectedReference = useCallback(
    (text: SavedText) => {
      handleAnalyzeReference(savedTextToReferenceItem(text))
    },
    [handleAnalyzeReference]
  )

  const handleRemoveReference = useCallback(
    (id: string) => {
      removeSelectedReference(id)
    },
    [removeSelectedReference]
  )

  // ========================================================================
  // 3-4. 렌더링
  // ========================================================================
  return (
    <div className={styles.refCollapsible}>
      {/* 토글 버튼 */}
      <button
        className={styles.refCollapseToggle}
        onClick={handleToggle}
        aria-expanded={!isCollapsed}
        aria-controls="ref-link-content"
      >
        <span
          className={`${styles.refToggleIcon}${!isCollapsed ? ` ${styles.expanded}` : ''}`}
        >
          ▶
        </span>
        <span className={styles.refToggleText}>
          참고 레퍼런스 선택 (선택사항)
        </span>
        <span className={styles.refToggleCount}>
          ({selectedReferences.length}개 선택됨)
        </span>
      </button>

      {/* 접히는 콘텐츠 */}
      <div
        id="ref-link-content"
        className={`${styles.refCollapseContent}${!isCollapsed ? ` ${styles.refExpanded}` : ''}`}
        aria-hidden={isCollapsed}
      >
        <div className={styles.refLinkSection}>
          {/* 레퍼런스 선택 버튼 */}
          <button
            className={styles.refSelectBtn}
            onClick={handleOpenLoader}
            disabled={selectedReferences.length >= 3}
          >
            <span>🔗</span>
            <span>레퍼런스 선택</span>
            <span className={styles.refSelectCount}>
              ({selectedReferences.length}개 선택됨
              {selectedReferences.length >= 3 ? ' - 최대' : ''})
            </span>
          </button>

          {/* 선택된 레퍼런스 태그 */}
          {selectedReferences.length > 0 && (
            <div className={styles.refTags} role="list">
              {selectedReferences.map((ref) => (
                <div key={ref.id} className={styles.refTag} role="listitem">
                  <span className={styles.refTagText}>{ref.title}</span>
                  <div className={styles.refTagActions}>
                    <button
                      className={styles.refTagAnalyze}
                      onClick={() => handleAnalyzeSelectedReference(ref)}
                      aria-label={`${ref.title} 분석`}
                    >
                      분석
                    </button>
                    <button
                      className={styles.refTagRemove}
                      onClick={() => handleRemoveReference(ref.id)}
                      aria-label={`${ref.title} 제거`}
                    >
                      &times;
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ReferenceLoader 모달 */}
      <ReferenceLoader
        isOpen={isLoaderOpen}
        onClose={handleCloseLoader}
        onSelect={handleSelectReference}
        onAnalyze={handleAnalyzeReference}
        showAnalysisAction
      />

      <ReferenceAnalysisModal
        isOpen={isAnalysisOpen}
        source={analysisSource}
        onClose={() => setIsAnalysisOpen(false)}
      />
    </div>
  )
}

export default ReferenceSelectionPanel
